import { CCard, CCardBody, CCardFooter, CCardText, CCardTitle } from "@coreui/react-pro";
import { Sparkline } from "../../components/common/Sparkline";
import { AltSymbolIcon, DirectionIcon, RiskIcon } from "@scryerlabs/ui";
import { TradingStrategyDirection } from "../../api/hooks/useTradingStrategies";
import CIcon from "@coreui/icons-react";
import { cilChevronRight } from "@coreui/icons";
import { Key } from "./HelpExampleKey";

/**
 * A worked example of a browse card, keyed 1-8 so the article can name each part.
 *
 * The marks come from the real icon components, so a redrawn icon reaches the help centre with
 * the catalogue — the same reason HelpMark renders them rather than exported SVGs. The rest of the
 * card is rebuilt here from literals rather than driven through StrategyCard: that component
 * navigates on click, links each side to its own page and resolves its pills and risk label through the
 * reference API, none of which an article wants. A help page can be the first page of the session,
 * and an unloaded reference list renders "-", so an example built on those hooks would show a card
 * with two empty pills.
 *
 * The cost is that the layout is a copy and can drift from the real card. What the article makes
 * claims about is the band — the tickers and the three marks — and that is the part sourced from
 * the components rather than restated.
 */

/** Carries all three marks, so the article has one card to point at for every one of them. */
const CURVE = [
  1, 1.1, 1.05, 1.3, 1.25, 1.6, 1.9, 1.8, 2.3, 2.2, 2.9, 3.4, 3.2, 4.1, 4.6,
  4.4, 5.5, 6.3, 6.0, 7.4,
];

// The same tracks as the real card's side rows, so the example lines up the same way.
const SIDE_COLUMNS = "minmax(0, 1fr) 4.75rem 4rem 3rem 1rem";

const SIDES = [
  {
    direction: TradingStrategyDirection.LongAndShort,
    label: "Long & short",
    shortLabel: "Both",
    mark: "Trades long and short",
    avg: "+38.0%",
    drawdown: "-22.4%",
    price: "$19",
  },
  {
    direction: TradingStrategyDirection.LongOnly,
    label: "Long only",
    shortLabel: "Long",
    mark: "Trades long only",
    avg: "+31.5%",
    drawdown: "-24.1%",
    price: "$14",
  },
];

export function HelpExampleCard() {
  return (
    <figure className="help-example-card">
      {/* Not a link and not focusable: the real card is a button that navigates, and an example
          that went somewhere would be a broken promise on a page explaining what it is. */}
      <CCard style={{ maxWidth: "23rem" }}>
        <div className="strategy-band d-flex align-items-center justify-content-between gap-2">
          <div className="d-flex align-items-center gap-2 text-truncate">
            <span className="font-monospace fw-medium text-truncate">
              QQQ
              <Key n={1} />
            </span>
            <span className="strategy-band-sep" aria-hidden />
            <span className="font-monospace small" style={{ color: "var(--sl-positive)" }}>
              TQQQ
            </span>
            <span className="font-monospace small" style={{ color: "var(--sl-negative)" }}>
              SQQQ
              <Key n={2} />
            </span>
            <span className="strategy-band-timeframe">1D</span>
            <Key n={3} />
          </div>

          <div className="d-flex align-items-center gap-1 flex-shrink-0">
            <DirectionIcon
              direction={TradingStrategyDirection.LongAndShort}
              label="Trades long and short"
            />
            <Key n={4} />
            <AltSymbolIcon label="Alt-Symbol Boost" />
            <Key n={5} />
            <RiskIcon riskProfile={3} label="Aggressive risk" />
            <Key n={6} />
          </div>
        </div>

        <CCardBody className="d-flex flex-column">
          <CCardTitle>Example Strategy</CCardTitle>
          <CCardText className="text-body-secondary">
            A made-up strategy, here only to be pointed at.
          </CCardText>

          <div className="mt-auto">
            <div className="text-body-secondary" style={{ opacity: 0.75 }}>
              <Sparkline data={CURVE} />
              <Key n={7} />
            </div>
          </div>
        </CCardBody>

        <CCardFooter className="bg-body-tertiary">
          <div className="strategy-sides">
            <div className="strategy-sides-head" style={{ gridTemplateColumns: SIDE_COLUMNS }}>
              <span>
                Side
                <Key n={8} />
              </span>
              <span className="text-end text-nowrap">Avg / yr</span>
              <span className="text-end text-nowrap">Max DD</span>
              <span className="text-end text-nowrap">Per mo</span>
              <span />
            </div>
            {SIDES.map((side) => (
              <div key={side.label} className="strategy-side-row" style={{ gridTemplateColumns: SIDE_COLUMNS }}>
                <span className="d-inline-flex align-items-center gap-1 text-nowrap fw-medium">
                  <DirectionIcon direction={side.direction} label={side.mark} size={16} />
                  <span className="side-words-full">{side.label}</span>
                  <span className="side-words-short">{side.shortLabel}</span>
                </span>
                <span className="text-end text-success">{side.avg}</span>
                <span className="text-end text-danger">{side.drawdown}</span>
                <span className="text-end">{side.price}</span>
                <CIcon icon={cilChevronRight} className="card-link-chevron" aria-hidden />
              </div>
            ))}
          </div>
        </CCardFooter>
      </CCard>

      <figcaption>
        An example. <strong>Not a real strategy</strong> — the name, the prices and the figures are
        invented.
      </figcaption>
    </figure>
  );
}
